import * as sdk from '@amplitude/unified';
import { CMS_PATH_PREFIXES } from './cms-paths.generated';
export type AmplitudePageProps = {
    tracking?: {
        cookieConsent?: { enabled?: boolean } | null;
        consent?: { given?: boolean; categories?: { analytics?: boolean } | null } | null;
    } | null;
    auth?: { user?: { id?: number | string | null } | null } | null;
};
const INTERNAL = [
    ...CMS_PATH_PREFIXES,
    '/page-builder',
    '/composer',
    '/routes',
    '/data-sources',
    '/qa/parity',
    '/prototype',
    '/design-system',
    '/system-explorer',
    '/notifications',
    '/blueprint',
    '/agent/social-media',
    '/my-analytics',
    '/preview/page',
    '/media',
    '/tags',
    '/system-pages',
    '/login',
    '/forgot-password',
    '/reset-password',
    '/author/setup',
    '/verify-email',
    '/confirm-password',
    '/two-factor',
    '/two-factor-challenge',
    '/auth/saml',
    '/symposium/vendor',
    '/newsletter/confirm',
    '/press-kit',
    '/share/d',
    '/share/booth',
    '/share/campaign',
    '/share/printer',
];
const COLLECTORS = [
    '@amplitude/plugin-form-interaction-tracking-browser',
    '@amplitude/plugin-file-download-tracking-browser',
    '@amplitude/plugin-page-view-tracking-browser',
    '@amplitude/plugin-autocapture-browser',
];
type EventStorage = NonNullable<NonNullable<sdk.UnifiedOptions['analytics']>['storageProvider']>;
type StoredEvent = NonNullable<Awaited<ReturnType<EventStorage['get']>>>[number];
const PUBLIC_PROVENANCE = '[CMS Privacy] Public Page';
export const isCms = (value: string) => {
    try {
        const url = new URL(value, location.origin);
        if (url.origin !== location.origin) return false;
        const path = decodeURIComponent(url.pathname);
        if (path === '/email/subscribe') return false;
        return INTERNAL.some((prefix) => path === prefix || path.startsWith(prefix + '/'));
    } catch {
        return true;
    }
};
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
function primaryUrl(event: Record<string, unknown>): string | null {
    const props = event.event_properties;
    if (!object(props)) return null;
    for (const key of ['[Amplitude] Page Location', '[Amplitude] Page URL', 'page_location', 'page_url', PUBLIC_PROVENANCE])
        if (typeof props[key] === 'string') return props[key] as string;
    return null;
}
function scrub(value: unknown, depth = 0): unknown {
    if (depth > 15) return undefined;
    if (typeof value === 'string' && /^(https?:\/\/|\/)/.test(value) && isCms(value)) return undefined;
    if (Array.isArray(value)) return value.map((item) => scrub(item, depth + 1)).filter((item) => item !== undefined);
    if (!object(value)) return value;
    return Object.fromEntries(
        Object.entries(value)
            .map(([key, item]) => [key, scrub(item, depth + 1)])
            .filter(([, item]) => item !== undefined),
    );
}
export function safeStoredEvent(event: unknown): StoredEvent | null {
    if (!object(event) || typeof event.event_type !== 'string') return null;
    const source = primaryUrl(event);
    // SDK URLs are absolute; root-relative public URLs also occur in legacy events.
    // Bare paths, invisible characters and scheme-relative values have unknown provenance.
    if (
        !source ||
        source !== source.trim() ||
        Array.from(source).some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127) ||
        !(/^https?:\/\//i.test(source) || /^\/(?![\\/])/.test(source)) ||
        isCms(source)
    )
        return null;
    try {
        if (new URL(source, location.origin).origin !== location.origin) return null;
    } catch {
        return null;
    }
    return scrub(event) as StoredEvent;
}
export function remapProviderResponse<T>(response: T, indexes: number[], originalCount: number): T {
    if (!object(response) || !object(response.body)) return response;
    const mapIndexes = (value: unknown) =>
        Array.isArray(value)
            ? value.filter((index) => Number.isInteger(index) && index >= 0 && index < indexes.length).map((index) => indexes[index])
            : [];
    const mapFields = (value: unknown) =>
        object(value) ? Object.fromEntries(Object.entries(value).map(([key, list]) => [key, mapIndexes(list)])) : {};
    const body = { ...response.body };
    if (response.status === 'invalid') {
        for (const name of ['eventsWithInvalidFields', 'eventsWithMissingFields', 'eventsWithInvalidIdLengths']) body[name] = mapFields(body[name]);
        const retained = new Set(indexes);
        body.silencedEvents = [
            ...mapIndexes(body.silencedEvents),
            ...Array.from({ length: originalCount }, (_, index) => index).filter((index) => !retained.has(index)),
        ];
    }
    if (response.status === 'rate_limit') body.throttledEvents = mapIndexes(body.throttledEvents);
    return { ...response, body } as T;
}
export function createPrivacyAmplitude(key: string, testOptions: Record<string, unknown> = {}) {
    const client = sdk.createInstance();
    const ownedKey = 'AMP_unsent_' + key.slice(0, 10);
    let initialized = false,
        blocked = true,
        starting: Promise<void> | null = null;
    let actual: { plugin(name: string): unknown } | null = null;
    const removed = new Map<string, unknown>();
    let transition = 0;
    let deliveryAllowed = false;
    let lastUserId: string | undefined;
    const storage: EventStorage = {
        isEnabled: async () => true, // This filtering provider remains active even if persistence is unavailable.
        get: async (name: string) => {
            if (name !== ownedKey) return [];
            let raw: unknown;
            try {
                raw = JSON.parse(localStorage.getItem(name) ?? '[]');
            } catch {
                raw = [];
            }
            const safe = Array.isArray(raw) ? raw.map(safeStoredEvent).filter((event): event is NonNullable<typeof event> => event !== null) : [];
            try {
                localStorage.setItem(name, JSON.stringify(safe));
            } catch {
                /* no unsafe fallback */
            }
            return safe;
        },
        set: async (name: string, events: unknown[]) => {
            if (name !== ownedKey) return;
            const safe = Array.isArray(events)
                ? events.map(safeStoredEvent).filter((event): event is NonNullable<typeof event> => event !== null)
                : [];
            try {
                localStorage.setItem(name, JSON.stringify(safe));
            } catch {
                /* filtered delivery remains possible without storage */
            }
        },
        getRaw: async (name: string) => (name === ownedKey ? JSON.stringify(await storage.get(name)) : undefined),
        reset: async () => {
            await storage.remove(ownedKey);
        },
        remove: async (name: string) => {
            if (name === ownedKey) {
                try {
                    localStorage.setItem(name, '[]');
                } catch {
                    /* safe no-op */
                }
            }
        },
    };
    client.add({
        name: 'cms-privacy-before',
        type: 'before',
        setup: async (_config, instance) => {
            const candidate = instance as unknown as { plugin?: unknown };
            if (typeof candidate.plugin !== 'function') throw new Error('SDK plugin access unavailable');
            actual = instance as unknown as NonNullable<typeof actual>;
            if (blocked || isCms(location.href)) _config.optOut = true;
            // Attribution bypasses the event queue. Filter only this key's campaign values,
            // while passing identity/session and every other storage operation unchanged.
            const transport = _config.transportProvider;
            _config.transportProvider = {
                send: async (url, payload, compression) => {
                    if (!deliveryAllowed) return null;
                    const indexes: number[] = [];
                    const events = payload.events.flatMap((event, index) => {
                        const safe = safeStoredEvent(event);
                        if (!safe) return [];
                        indexes.push(index);
                        return [safe];
                    });
                    if (!events.length) return null;
                    const response = await transport.send(url, { ...payload, events }, compression);
                    return remapProviderResponse(response, indexes, payload.events.length);
                },
            };
            const delegate = _config.cookieStorage;
            const campaignKeys = new Set(['AMP_MKTG_' + key.slice(0, 10), 'AMP_MKTG_ORIGINAL_' + key.slice(0, 10)]);
            const campaignSafe = (value: unknown) => (blocked || isCms(location.href) ? undefined : scrub(value));
            _config.cookieStorage = {
                isEnabled: () => delegate.isEnabled(),
                get: async (name) => {
                    const value = await delegate.get(name);
                    if (!campaignKeys.has(name)) return value;
                    if (
                        name.includes('MKTG_ORIGINAL') &&
                        (!object(value) || typeof value.referrer !== 'string' || !value.referrer || isCms(value.referrer))
                    )
                        return undefined;
                    return campaignSafe(value) as typeof value;
                },
                getRaw: async (name) => (campaignKeys.has(name) ? undefined : delegate.getRaw(name)),
                set: async (name, value) => {
                    if (!campaignKeys.has(name)) return delegate.set(name, value);
                    const safe = campaignSafe(value);
                    if (safe !== undefined) await delegate.set(name, safe as typeof value);
                },
                remove: (name) => delegate.remove(name),
                reset: () => delegate.reset(),
            };
        },
        execute: async (event) => {
            if (blocked || isCms(location.href)) {
                return null;
            }
            const existing = primaryUrl(event as unknown as Record<string, unknown>);
            if (existing && isCms(existing)) {
                return null;
            }
            const stamped = { ...event, event_properties: { ...event.event_properties, [PUBLIC_PROVENANCE]: location.href } };
            return safeStoredEvent(stamped) as typeof event | null;
        },
    });
    const removeCollectors = () => {
        for (const name of COLLECTORS) {
            const plugin = actual?.plugin(name);
            if (plugin) {
                removed.set(name, plugin);
                void client.remove(name).promise;
            }
        }
    };
    const suspend = () => {
        blocked = true;
        transition++;
        client.setOptOut(true);
        removeCollectors();
    };
    const allowed = (props: AmplitudePageProps | undefined) =>
        !props?.tracking?.cookieConsent?.enabled || !!(props?.tracking?.consent?.given && props?.tracking?.consent?.categories?.analytics);
    const afterPage = async (props: AmplitudePageProps | undefined) => {
        deliveryAllowed = allowed(props);
        if (isCms(location.href) || !allowed(props)) {
            suspend();
            await storage.get(ownedKey);
            return;
        }
        const generation = ++transition;
        blocked = false;
        if (!initialized) {
            if (!starting) {
                starting = client
                    .initAll(key, {
                        serverZone: 'US',
                        analytics: {
                            minIdLength: 1,
                            userId: props?.auth?.user?.id ? String(props.auth.user.id) : undefined,
                            autocapture: {
                                attribution: true,
                                pageViews: true,
                                sessions: true,
                                formInteractions: true,
                                fileDownloads: true,
                                elementInteractions: true,
                                pageUrlEnrichment: false,
                            },
                            storageProvider: storage,
                            ...testOptions,
                        },
                        sessionReplay: { sampleRate: 0 },
                    })
                    .then(() => {
                        initialized = true;
                        // Initialization may finish after a newer CMS transition. Reconcile
                        // with current policy without changing its ownership generation.
                        if (blocked || isCms(location.href)) {
                            client.setOptOut(true);
                            removeCollectors();
                        }
                    })
                    .catch(() => {
                        starting = null;
                        suspend();
                    });
            }
            await starting;
        }
        if (!initialized) return;
        if (generation !== transition) return;
        if (isCms(location.href) || !allowed(props)) {
            suspend();
            return;
        }
        const userId = props?.auth?.user?.id ? String(props.auth.user.id) : undefined;
        if (userId !== lastUserId) {
            if (userId) client.setUserId(userId);
            else client.reset();
            lastUserId = userId;
        }
        client.setOptOut(false);
        for (const [name, plugin] of removed) {
            await client.add(plugin as Parameters<typeof client.add>[0]).promise;
            if (generation !== transition) {
                // A late installation must obey the newest policy, but cannot suspend
                // a newer public owner or advance that owner's generation.
                if (blocked || isCms(location.href)) removeCollectors();
                return;
            }
            removed.delete(name);
            if (isCms(location.href) || !allowed(props)) {
                suspend();
                return;
            }
        }
    };
    const beforePage = (url: string, props: AmplitudePageProps | undefined) => {
        deliveryAllowed = allowed(props);
        if (isCms(url) || !deliveryAllowed) suspend();
    };
    const popstate = () => {
        if (isCms(location.href)) suspend();
    };
    window.addEventListener('popstate', popstate, true);
    return {
        afterPage,
        beforePage,
        suspend,
        ownedKey,
        client,
        isBlocked: () => blocked,
        stop: () => {
            suspend();
            window.removeEventListener('popstate', popstate, true);
        },
    };
}
